import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EmailCaptureConfig } from '@/types/vsleads/emailCaptureTypes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowsVertical, ArrowsHorizontal } from '@phosphor-icons/react';
import { SkipForward } from 'lucide-react';

interface LayoutConfigTabProps {
  form: UseFormReturn<EmailCaptureConfig>;
  isCapturePassword?: boolean;
}

export function LayoutConfigTab({ form, isCapturePassword = false }: LayoutConfigTabProps) {
  useLanguage();
  const orientation = form.watch('orientation');
  const texts = form.watch('texts');

  return (
    <div className="space-y-6">
      {/* Card: Orientação dos Campos */}
      {/* <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[17px] font-semibold text-[#101828]">Orientação</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            Como os campos serão dispostos no formulário
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={orientation}
            onValueChange={(value: 'vertical' | 'horizontal') =>
              form.setValue('orientation', value, { shouldDirty: true })
            }
          >
            <div className="grid grid-cols-2 gap-4">
              <Label
                htmlFor="orientation-vertical"
                className="flex flex-col items-center gap-3 cursor-pointer rounded-lg border-2 p-6 hover:border-primary/50 transition-all has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              >
                <RadioGroupItem value="vertical" id="orientation-vertical" className="sr-only" />
                <ArrowsVertical className="h-10 w-10 text-primary" />
                <span className="font-medium">Vertical</span>
              </Label>

              <Label
                htmlFor="orientation-horizontal"
                className="flex flex-col items-center gap-3 cursor-pointer rounded-lg border-2 p-6 hover:border-primary/50 transition-all has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              >
                <RadioGroupItem value="horizontal" id="orientation-horizontal" className="sr-only" />
                <ArrowsHorizontal className="h-10 w-10 text-primary" />
                <span className="font-medium">Horizontal</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card> */}

      {/* Card: Título e Descrição */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[17px] font-semibold text-[#101828]">{translateInterfaceText("Título")}</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            {translateInterfaceText("Texto principal do formulário")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="headline">{translateInterfaceText("Título do formulário")}</Label>
            <Input
              id="headline"
              value={texts.headline}
              onChange={(e) =>
                form.setValue('texts.headline', e.target.value, { shouldDirty: true })
              }
              placeholder={translateInterfaceText("Ex: Libere o acesso ao vídeo")}
              className="mt-2"
            />
          </div>

          {/* <div>
            <Label htmlFor="subheadline">Descrição</Label>
            <Input
              id="subheadline"
              value={texts.subheadline}
              onChange={(e) =>
                form.setValue('texts.subheadline', e.target.value, { shouldDirty: true })
              }
              placeholder="Ex: Preencha os dados abaixo"
              className="mt-2"
            />
          </div> */}
        </CardContent>
      </Card>

      {/* Card: Senha - Somente para captura por senha */}
      {isCapturePassword && (
        <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[17px] font-semibold text-[#101828]">{translateInterfaceText("Senha de Acesso")}</CardTitle>
            <CardDescription className="text-[13px] text-[#667085]">
              {translateInterfaceText("Defina a senha que o usuário precisará digitar para acessar o vídeo")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="password-value">{translateInterfaceText("Senha *")}</Label>
              <Input
                id="password-value"
                type="text"
                value={texts.footer}
                onChange={(e) =>
                  form.setValue('texts.footer', e.target.value, { shouldDirty: true })
                }
                placeholder={translateInterfaceText("Digite a senha de acesso")}
                className="mt-2"
              />
              <p className="text-[12px] text-[#6B7280] mt-2">
                {translateInterfaceText("Esta será a senha que os usuários precisarão digitar para desbloquear o vídeo")}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card: Rodapé - Ocultar quando for captura por senha */}
      {!isCapturePassword && (
        <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[17px] font-semibold text-[#101828]">{translateInterfaceText("Rodapé")}</CardTitle>
            <CardDescription className="text-[13px] text-[#667085]">
              {translateInterfaceText("Adicione uma mensagem extra no rodapé (opcional)")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="footer">{translateInterfaceText("Mensagem Adicional")}</Label>
              <Input
                id="footer"
                value={texts.footer}
                onChange={(e) =>
                  form.setValue('texts.footer', e.target.value, { shouldDirty: true })
                }
                placeholder={translateInterfaceText("Ex: Seus dados estão seguros conosco")}
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card: Botão */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[17px] font-semibold text-[#101828]">{translateInterfaceText("Botão")}</CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            {translateInterfaceText("Configure os textos do botão de ação")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="button-text">{translateInterfaceText("Texto do botão principal")}</Label>
            <Input
              id="button-text"
              value={texts.buttonText}
              onChange={(e) =>
                form.setValue('texts.buttonText', e.target.value, { shouldDirty: true })
              }
              placeholder={translateInterfaceText("Ex: Continuar Assistindo")}
              className="mt-2"
            />
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
